import { useLocation, useParams } from 'wouter';
import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useNavigationStore } from '../stores/navigationStore';

/**
 * 🚀 Hook de navigation unifié pour Wouter
 * Hook de navigation unifié utilisant Wouter
 */
export const useWouterNavigate = () => {
  const [, setLocation] = useLocation();
  const setEventModalState = useNavigationStore((s) => s.setEventModalState);
  const setPreviousPath = useNavigationStore((s) => s.setPreviousPath);

  const navigate = useCallback((
    to: string | number,
    options?: {
      replace?: boolean;
      state?: any;
    }
  ) => {
    // Handle back navigation
    if (typeof to === 'number') {
      if (to === -1) {
        window.history.back();
      } else {
        window.history.go(to);
      }
      return;
    }

    // Store state if provided
    if (options?.state) {
      setEventModalState(options.state);
    }

    // Store current path for back navigation
    if (!options?.replace) {
      setPreviousPath(window.location.pathname);
    }

    // Use Wouter's native navigation
    // Note: Wouter's replace might not work perfectly, but it's more stable
    // than manual history manipulation which causes massive re-renders
    setLocation(to, options?.replace ? { replace: true } as any : undefined);
  }, [setLocation, setEventModalState, setPreviousPath]);

  return navigate;
};

/**
 * 🚀 Hook combo pour migration facile
 * Remplace useNavigate + useParams + useLocation
 *
 * ✅ PERF FIX: useLocation() appelé UNE seule fois (au lieu de 2x avec useWouterNavigate)
 * ✅ PERF FIX: useNavigationStore subscriptions consolidées via useShallow (3 → 1)
 * ✅ PERF FIX: location object mémorisé pour éviter nouvel objet à chaque render
 * Résultat: 3 souscriptions useSyncExternalStore au lieu de 6
 */
export const useWouterRouter = () => {
  const [location, setLocation] = useLocation();
  const params = useParams();

  // ✅ PERF: 1 seule subscription au lieu de 3 séparées
  const { eventModalState, setEventModalState, setPreviousPath } = useNavigationStore(
    useShallow(s => ({
      eventModalState: s.eventModalState,
      setEventModalState: s.setEventModalState,
      setPreviousPath: s.setPreviousPath
    }))
  );

  // ✅ PERF: Navigate inline (évite le doublon useLocation() de useWouterNavigate)
  const navigate = useCallback((
    to: string | number,
    options?: { replace?: boolean; state?: any }
  ) => {
    if (typeof to === 'number') {
      if (to === -1) window.history.back();
      else window.history.go(to);
      return;
    }
    if (options?.state) setEventModalState(options.state);
    if (!options?.replace) setPreviousPath(window.location.pathname);
    setLocation(to, options?.replace ? { replace: true } as any : undefined);
  }, [setLocation, setEventModalState, setPreviousPath]);

  // ✅ PERF: Mémoriser le sous-objet location (évite nouvel objet chaque render)
  const locationObj = useMemo(() => ({
    pathname: location,
    state: eventModalState,
    search: window.location.search,
    hash: window.location.hash
  }), [location, eventModalState]);

  return {
    navigate,
    params,
    location: locationObj
  };
};